import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Stack,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import EmailIcon from "@mui/icons-material/Email";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { toast } from "react-toastify";

import AppEmailModal from "../../common/AppEmailModal";
import AppButton from "../../buttons/AppButton";
import AppCircularProgress from "../../common/AppCircularProgress";
import {
  getFiscalInvoicePdf,
  emailFiscalInvoice,
} from "../../../redux/actions/fiscalActions";
import { clearPdfUrl } from "../../../redux/slices/fiscal";

const FiscalInvoicePdfTab = ({ invoiceNumber }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { pdfUrl, loadingInvoicePdf, errorInvoicePdf, loadingInvoiceEmail } =
    useSelector((state) => state.fiscal);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  useEffect(() => {
    if (invoiceNumber && !pdfUrl && !loadingInvoicePdf) {
      dispatch(getFiscalInvoicePdf(invoiceNumber));
    }
    return () => {
      dispatch(clearPdfUrl());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, invoiceNumber]);

  const handleDownload = () => {
    if (!pdfUrl) return;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `eTIMS_Tax_Invoice_${invoiceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    if (!pdfUrl) return;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 2000);
      }, 100);
    };
  };

  const handleEmailSubmit = async (email) => {
    await dispatch(emailFiscalInvoice(invoiceNumber, email));
    setEmailModalOpen(false); // Assume successful if error handling is internal
  };

  const renderDesktopActions = () => (
    <Stack direction="row" spacing={1.5} mb={3} justifyContent="flex-end">
      <AppButton
        variant="outlined"
        color="primary"
        startIcon={<ShareIcon />}
        onClick={() => toast.success("Link copied!")}
      >
        Share Link
      </AppButton>
      <AppButton
        variant="outlined"
        color="secondary"
        startIcon={<EmailIcon />}
        onClick={() => setEmailModalOpen(true)}
      >
        Email
      </AppButton>
      <AppButton
        variant="contained"
        color="warning"
        startIcon={<FileDownloadIcon />}
        onClick={handleDownload}
        disabled={!pdfUrl || loadingInvoicePdf}
      >
        Download
      </AppButton>
      <AppButton
        variant="contained"
        color="primary"
        startIcon={<PrintIcon />}
        onClick={handlePrint}
        disabled={!pdfUrl || loadingInvoicePdf}
      >
        Print
      </AppButton>
    </Stack>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {isMobile ? null : renderDesktopActions()}

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: "650px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "#1b1b1b" : "#f1f5f9",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {loadingInvoicePdf ? (
          <AppCircularProgress text="Loading eTIMS PDF..." />
        ) : errorInvoicePdf ? (
          <>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography color="error">{errorInvoicePdf}</Typography>
            <AppButton
              variant="outlined"
              color="error"
              sx={{ mt: 2 }}
              onClick={() => dispatch(getFiscalInvoicePdf(invoiceNumber))}
            >
              Retry
            </AppButton>
          </>
        ) : pdfUrl ? (
          <iframe
            src={`${pdfUrl}#toolbar=0&view=FitH`}
            title={`Fiscal Invoice PDF ${invoiceNumber}`}
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        ) : null}
      </Paper>

      <AppEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSubmit={handleEmailSubmit}
        loading={loadingInvoiceEmail}
        title="Email eTIMS Invoice"
        description={`Send a secure copy of eTIMS tax invoice to an email.`}
      />
    </Box>
  );
};
export default FiscalInvoicePdfTab;
